import React, {useCallback, useMemo, useState} from 'react';
import {ModelDTO} from 'hemwb-api/dist/models/ModelDTO';
import {modelPublish} from '../../../../../store/model';
import {IssueSeverity, useValidateModel} from '../../../../../store/model/validator';
import {useMounted} from '../../../../../hooks/useMounted';
import {useDispatch} from 'react-redux';
import {trackModelPublished} from '../../../../../tracking/tracking';
import {messageAdd, MessageTypes} from '../../../../../store/message';
import {Alert, Button, Typography} from 'antd';
import {ExportOutlined} from '@ant-design/icons';
import {tid} from '../../../../../testUtils';
import ModelIssueList from '../../../modelUpload/StepNameConfirmation/ModelIssueList';
import SuspenseNull from '../../../../core/Suspense/SuspenseNull';
import {MODEL_ACTION_MENU_IDS} from '../../../../../test/types';

const {Text, Title} = Typography;
const headingTitleStyle = {margin: '8px 0 1rem 0', fontSize: '2.2rem', lineHeight: '1.6rem', fontWeight: 700 as const};
const headingSubtitleStyle = {fontSize: '2rem', lineHeight: '2.6rem', fontWeight: 400 as const};

export type ModelPublishProps = {
  model: ModelDTO;
  onSuccess?: () => void;
  onCancel?: () => void;
};

const stepToMenuButton = [
  MODEL_ACTION_MENU_IDS.EDIT_FILES,
  MODEL_ACTION_MENU_IDS.EDIT_METADATA,
  MODEL_ACTION_MENU_IDS.EDIT_LINEAGE,
  MODEL_ACTION_MENU_IDS.EDIT_PERMISSIONS,
  MODEL_ACTION_MENU_IDS.EDIT_SUFFIX,
];

const ModelPublish: React.FC<ModelPublishProps> = ({model, onSuccess, onCancel}) => {
  const dispatch = useDispatch();
  const issues = useValidateModel(model);
  const errors = useMemo(() => {
    return issues?.filter((i) => i.severity >= IssueSeverity.ERROR_PUBLISH);
  }, [issues]);

  const [submitting, setSubmitting] = useState(false);
  const mounted = useMounted();

  const handleSubmit = useCallback(() => {
    setSubmitting(true);
    modelPublish(dispatch, model.uuid)
      .then(() => {
        dispatch(messageAdd('Model has been published.', MessageTypes.SUCCESS));
        trackModelPublished(model);
        onSuccess?.();
      })
      .catch(() => null)
      .finally(() => {
        mounted.current && setSubmitting(false);
      });
  }, [dispatch, mounted, model, onSuccess]);

  const handleClickOnIssue = useCallback(
    (step: number) => {
      const el = document.querySelector(`[data-testid='${stepToMenuButton[step]}']`) as HTMLDivElement;
      if (el) {
        el.click();
        onCancel?.();
      }
    },
    [onCancel],
  );

  return (
    <>
      <div style={{display: 'flex', gap: '1rem', alignItems: 'flex-start'}}>
        {/* Left: Icon */}
        <div style={{flexShrink: 0}}>
          <ExportOutlined style={{color: '#00857c', fontSize: '3.2rem'}} />
        </div>

        {/* Right: Content */}
        <div style={{flex: 1, marginLeft: '1rem'}}>
          {/* Title */}
          <Title level={5} style={headingTitleStyle}>
            Publish Model Entry
          </Title>

          {/* Subtitle */}
          <div style={{marginBottom: '1.2rem'}}>
            <Text style={headingSubtitleStyle}>This operation can&apos;t be undone!</Text>
          </div>

          {/* Issues List */}
          <SuspenseNull>
            {issues && (
              <ModelIssueList
                issues={issues}
                setActiveStep={handleClickOnIssue}
                errorLevel={IssueSeverity.ERROR_PUBLISH}
              />
            )}
          </SuspenseNull>

          {errors && errors.length > 0 && (
            <Alert
              type="warning"
              showIcon
              message="Red issues are required to fix in order to be able to proceed. You can navigate to the desired step by clicking on any issue."
              style={{marginTop: '1.2rem'}}
            />
          )}

          <div style={{display: 'flex', justifyContent: 'flex-end', marginTop: '2.4rem', gap: '0.8rem'}}>
            <Button onClick={onCancel} {...tid('dialog-confirm-publish-btn-cancel')}>
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleSubmit}
              loading={submitting}
              disabled={errors?.length !== 0}
              {...tid('dialog-confirm-publish-btn-submit')}
            >
              <span style={{display: 'inline-flex', alignItems: 'center', gap: '0.6rem'}}>
                Publish
                <ExportOutlined />
              </span>
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ModelPublish;
